import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Text, tokens } from '@fluentui/react-components';
import { useT } from '../../i18n';
import {
  createTelegramLinkToken,
  fetchTelegramLinkStatus,
  unlinkTelegram,
  type TelegramLinkToken,
} from '../../api/telegram-link.api';

/** Intervalo entre consultas del estado mientras espera la vinculación. */
export const TELEGRAM_POLL_INTERVAL_MS = 3_000;
/** Tiempo máximo esperando a que el usuario confirme el enlace en Telegram. */
export const TELEGRAM_POLL_TIMEOUT_MS = 120_000;

interface Props {
  /** Estado de vinculación conocido por la sesión (user.telegramId). */
  initialLinked: boolean;
}

type Pending = TelegramLinkToken | null;

/**
 * Sección "Telegram" del perfil: muestra si la cuenta está vinculada, genera el enlace
 * de vinculación, consulta su estado mientras no esté vinculada y permite desvincular.
 * Si el backend no tiene los endpoints (404) o no hay red, muestra un aviso localizado.
 */
export function TelegramLinkSection({ initialLinked }: Props) {
  const t = useT();
  const L = t.topBar.telegramLink;
  const queryClient = useQueryClient();

  const [linked, setLinked] = useState(initialLinked);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [expired, setExpired] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);

  // Sincroniza con la sesión si cambia desde fuera (p. ej. tras recargar el perfil).
  useEffect(() => { setLinked(initialLinked); }, [initialLinked]);

  // Sondeo del estado: cada 3 s, hasta 2 minutos.
  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    let elapsed = 0;
    const id = setInterval(async () => {
      elapsed += TELEGRAM_POLL_INTERVAL_MS;
      if (elapsed >= TELEGRAM_POLL_TIMEOUT_MS) {
        clearInterval(id);
        if (!cancelled) { setPending(null); setExpired(true); }
        return;
      }
      try {
        const status = await fetchTelegramLinkStatus(pending.token);
        if (cancelled) return;
        if (status.linked) {
          clearInterval(id);
          setLinked(true);
          setMaskedPhone(status.maskedPhone);
          setPending(null);
          void queryClient.invalidateQueries({ queryKey: ['me'] });
        }
      } catch {
        // Error transitorio: se reintenta en el siguiente ciclo hasta agotar el tiempo.
      }
    }, TELEGRAM_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [pending, queryClient]);

  const handleGenerate = async () => {
    setBusy(true);
    setExpired(false);
    setUnavailable(false);
    try {
      const data = await createTelegramLinkToken();
      setPending(data);
    } catch {
      setUnavailable(true);
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await unlinkTelegram();
      setLinked(false);
      setMaskedPhone(null);
      setConfirmingUnlink(false);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch {
      setUnavailable(true);
    } finally {
      setBusy(false);
    }
  };

  const openDeepLink = () => {
    if (pending) window.open(pending.deepLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="telegram-link-section" style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
      <Text weight="semibold" size={300}>{L.title}</Text>

      {linked ? (
        <>
          <Text size={200} style={{ color: '#2AABEE' }}>
            {L.linked}{maskedPhone ? ` · ${maskedPhone}` : ''}
          </Text>
          {confirmingUnlink ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
              <Text size={200}>{L.unlinkConfirm}</Text>
              <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                <Button size="small" appearance="primary" disabled={busy} onClick={handleUnlink}>
                  {L.unlinkConfirmBtn}
                </Button>
                <Button size="small" appearance="subtle" disabled={busy} onClick={() => setConfirmingUnlink(false)}>
                  {L.cancel}
                </Button>
              </div>
            </div>
          ) : (
            <Button size="small" appearance="subtle" disabled={busy} onClick={() => setConfirmingUnlink(true)}>
              {L.unlink}
            </Button>
          )}
        </>
      ) : pending ? (
        <>
          <Text size={200}>{L.waiting}</Text>
          <Button size="small" appearance="primary" onClick={openDeepLink}>{L.openTelegram}</Button>
        </>
      ) : (
        <>
          <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>{L.notLinked}</Text>
          <Button size="small" appearance="outline" disabled={busy} onClick={handleGenerate}>
            {L.link}
          </Button>
        </>
      )}

      {expired && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{L.expired}</Text>}
      {unavailable && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{L.unavailable}</Text>}
    </div>
  );
}
